/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Platform } from 'react-native';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  useAccessToken,
  useApiKey,
  useIsAllStar,
  useIsLoggedIn,
  useUserId,
} from '@airasia-rn/account/hooks';
import {
  QUERY_PERSIST_CACHE,
  useAppConfig,
  useDeviceInfo,
} from '@airasia-rn/ui';
import { useNearestAirportsCodes } from '@airasia-rn/flights/hooks';
import { RecommendationApiService } from '@airasia-rn/social';

import type {
  RecommendationApiItem,
  RecommendationApiResponse,
} from '../models/recommendTabResponse';
import {
  getRecommendationErrorInfo,
  RECOMMENDATION_NETWORK_ERROR_INFO,
  type RecommendationErrorInfo,
} from './recommendationErrorUtils';
import { useNetworkStatus } from './useNetworkStatus';
import { useHomepageBlocks } from './useHomepage';

const INITIAL_ROWS = 3;
const RECOMMENDATION_STALE_TIME = 30 * 60 * 1000;

interface UseForYouParams {
  pageSize?: number;
  enabled?: boolean;
}

const forYouKeys = {
  feed: (params: {
    country: string;
    city: string;
    nearestAirport: string;
    pageSize: number;
    currency: string;
  }) => [QUERY_PERSIST_CACHE, 'recommendation', 'for_you', params] as const,
};

export const useForYouFeed = ({
  pageSize = 20,
  enabled: isEnabled = true,
}: UseForYouParams = {}) => {
  const { isMobileWidth } = useDeviceInfo();
  const appConfig = useAppConfig();
  const api = useMemo(
    () => new RecommendationApiService(appConfig.config.env ?? 'stg'),
    [appConfig.config.env],
  );
  const accessTokenQuery = useAccessToken('homeForYouRecommendations');
  const { isLoggedIn } = useIsLoggedIn('homeForYouRecommendations');
  const userIdQuery = useUserId('homeForYouRecommendations');
  const isAllStar = useIsAllStar('homeForYouRecommendations');
  const apiKeyQuery = useApiKey();
  const nearestAirportQuery = useNearestAirportsCodes();

  const rowSize = useMemo(() => (isMobileWidth ? 2 : 4), [isMobileWidth]);
  const itemsPerBatch = useMemo(() => rowSize * INITIAL_ROWS, [rowSize]);
  const [visibleCount, setVisibleCount] = useState(itemsPerBatch);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshErrorInfo, setRefreshErrorInfo] =
    useState<RecommendationErrorInfo>();
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [loadMoreErrorInfo, setLoadMoreErrorInfo] =
    useState<RecommendationErrorInfo>();

  const { responseCurrency } = useHomepageBlocks();
  const userId = userIdQuery.data ?? '';
  const clientId = apiKeyQuery.data?.id ?? '';
  const apiKey = apiKeyQuery.data?.apiKey ?? '';
  const locale = appConfig.config.locale ?? 'en-gb';
  const currency = (
    appConfig.config.currency ||
    responseCurrency ||
    'USD'
  ).toUpperCase();
  const nearestAirportLatLong = nearestAirportQuery.data?.latlong ?? '';
  const location = useMemo(() => {
    const [lat, lon] = nearestAirportLatLong
      .split(',')
      .map((value) => Number(value.trim()));

    return {
      country:
        appConfig.config.geoId || nearestAirportQuery.data?.country || 'MY',
      city: nearestAirportQuery.data?.city ?? '',
      nearestAirport: nearestAirportQuery.data?.airport?.[0] ?? '',
      latitude: Number.isFinite(lat) ? lat : 0,
      longitude: Number.isFinite(lon) ? lon : 0,
    };
  }, [
    appConfig.config.geoId,
    nearestAirportQuery.data?.airport,
    nearestAirportQuery.data?.city,
    nearestAirportQuery.data?.country,
    nearestAirportLatLong,
  ]);

  const { isConnected, isNetworkStatusKnown } = useNetworkStatus();
  const canQueryRecommendations =
    isEnabled && Boolean(clientId) && Boolean(apiKey);
  const canUseNetwork =
    Platform.OS === 'web' || isNetworkStatusKnown ? isConnected : true;
  const isLocationSettled = !nearestAirportQuery.isLoading;
  const enabled =
    canQueryRecommendations &&
    Boolean(appConfig.config.currency || responseCurrency) &&
    isLocationSettled &&
    canUseNetwork;
  const queryKey = useMemo(
    () =>
      forYouKeys.feed({
        country: location.country,
        city: location.city,
        nearestAirport: location.nearestAirport,
        pageSize,
        currency,
      }),
    [
      location.country,
      location.city,
      location.nearestAirport,
      pageSize,
      currency,
    ],
  );

  const query = useInfiniteQuery<RecommendationApiResponse, Error>({
    queryKey,
    enabled,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const pageNumber = typeof pageParam === 'number' ? pageParam : 1;
      const clientKey = await apiKeyQuery.getToken();
      const resolvedClientId = clientKey?.id ?? '';
      const resolvedApiKey = clientKey?.apiKey ?? '';
      if (!resolvedClientId || !resolvedApiKey) {
        throw new Error('Missing API key');
      }

      // Guests keep the previous request, which may have no access token.
      // Signed-in requests must use a freshly resolved token.
      let resolvedAccessToken = accessTokenQuery.data ?? '';
      if (isLoggedIn) {
        resolvedAccessToken = (await accessTokenQuery.getToken()) ?? '';
        if (!resolvedAccessToken) {
          throw new Error('Missing access token');
        }
      }

      const response = await api.fetchForYou({
        accessToken: resolvedAccessToken,
        clientId: resolvedClientId,
        apiKey: resolvedApiKey,
        isAllStar,
        payload: {
          userId,
          page: pageNumber,
          pageSize,
          currency,
          locale,
          location,
          debug: false,
        },
      });

      return response;
    },
    getNextPageParam: (lastPage) =>
      lastPage.page * pageSize < lastPage.totalRecords
        ? lastPage.page + 1
        : undefined,
    staleTime: RECOMMENDATION_STALE_TIME,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...(Platform.OS === 'web'
      ? {}
      : {
          // Let TanStack revalidate when native connectivity recovers; the
          // enabled flag controls known-offline launches without an effect.
          refetchOnReconnect: 'always' as const,
        }),
    retry: 0,
    networkMode: 'always',
  });

  const allItems = useMemo<RecommendationApiItem[]>(() => {
    const seen = new Set<string>();
    return (query.data?.pages.flatMap((page) => page.data ?? []) ?? []).filter(
      (item) => {
        if (!item.lobItemId || seen.has(item.lobItemId)) return false;
        seen.add(item.lobItemId);
        return true;
      },
    );
  }, [query.data?.pages]);

  const prereqFailed =
    !apiKeyQuery.isLoading &&
    !accessTokenQuery.isLoading &&
    apiKeyQuery.isError;

  useEffect(() => {
    setVisibleCount((current) => {
      if (current < itemsPerBatch) return itemsPerBatch;
      return current;
    });
  }, [itemsPerBatch]);

  useEffect(() => {
    const pageCount = query.data?.pages.length ?? 0;
    if (query.dataUpdatedAt > 0 && pageCount <= 1) {
      setVisibleCount(itemsPerBatch);
      setRefreshErrorInfo(undefined);
      setLoadMoreError(false);
      setLoadMoreErrorInfo(undefined);
    }
  }, [query.dataUpdatedAt, query.data?.pages.length, itemsPerBatch]);

  const {
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    data: queryData,
  } = query;

  const clearLoadMoreError = useCallback(() => {
    setLoadMoreError(false);
    setLoadMoreErrorInfo(undefined);
  }, []);

  const setLoadMoreErrorState = useCallback(
    (errorInfo: RecommendationErrorInfo) => {
      setLoadMoreError(true);
      setLoadMoreErrorInfo(errorInfo);
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!canQueryRecommendations) return;

    try {
      setRefreshing(true);
      setVisibleCount(itemsPerBatch);
      setRefreshErrorInfo(undefined);
      clearLoadMoreError();
      const result = await refetch();
      setRefreshErrorInfo(
        result.isError ? getRecommendationErrorInfo(result.error) : undefined,
      );
    } finally {
      setRefreshing(false);
    }
  }, [canQueryRecommendations, clearLoadMoreError, itemsPerBatch, refetch]);

  const loadMore = useCallback(async () => {
    const nextVisible = visibleCount + itemsPerBatch;

    if (nextVisible <= allItems.length) {
      clearLoadMoreError();
      setVisibleCount(nextVisible);
      return;
    }

    if (visibleCount < allItems.length) {
      clearLoadMoreError();
      setVisibleCount(allItems.length);
      return;
    }

    if (
      !canQueryRecommendations ||
      !hasNextPage ||
      isFetching ||
      isFetchingNextPage
    )
      return;

    if (Platform.OS !== 'web' && isNetworkStatusKnown && !isConnected) {
      setLoadMoreErrorState(RECOMMENDATION_NETWORK_ERROR_INFO);
      return;
    }

    clearLoadMoreError();
    try {
      const previousPageCount = queryData?.pages.length ?? 0;
      const result = await fetchNextPage();
      const nextPageCount = result.data?.pages.length ?? previousPageCount;

      if (nextPageCount <= previousPageCount) {
        setLoadMoreErrorState({ code: undefined, message: 'Unknown error' });
        return;
      }

      setVisibleCount(nextVisible);
    } catch (error) {
      setLoadMoreErrorState(getRecommendationErrorInfo(error));
    }
  }, [
    visibleCount,
    itemsPerBatch,
    allItems.length,
    canQueryRecommendations,
    clearLoadMoreError,
    hasNextPage,
    isConnected,
    isFetching,
    isFetchingNextPage,
    isNetworkStatusKnown,
    fetchNextPage,
    queryData?.pages.length,
    setLoadMoreErrorState,
  ]);

  const retry = useCallback(() => {
    if (!canQueryRecommendations) return;
    void refetch();
  }, [canQueryRecommendations, refetch]);

  const items = allItems.slice(0, visibleCount);

  const showSeeMore =
    allItems.length > 0 && (visibleCount < allItems.length || hasNextPage);

  const loading =
    (accessTokenQuery.isLoading ||
      userIdQuery.isLoading ||
      apiKeyQuery.isLoading ||
      nearestAirportQuery.isLoading) &&
    items.length === 0;

  return {
    items,
    loading:
      loading || query.isLoading || (query.isFetching && allItems.length === 0),
    loadingMore: query.isFetchingNextPage,
    refreshErrorInfo,
    loadMoreError,
    loadMoreErrorInfo,
    error:
      (query.isError && !query.isFetching) ||
      (prereqFailed && allItems.length === 0),
    errorInfo: query.error
      ? getRecommendationErrorInfo(query.error)
      : undefined,
    loadMore,
    retry,
    showSeeMore,
    refreshing,
    refresh,
    imageRefreshKey: query.dataUpdatedAt,
  };
};
